'use client'

import { useEffect, useRef, type RefObject } from 'react'
import { analytics, type EventProperties } from '@/lib/analytics'

/**
 * Fire a one-shot impression event the first time the attached element is
 * meaningfully visible. Returns a ref to attach to the surface root.
 *
 * Contract: pass the SAME `source` string the surface submits as its signup
 * `source` so the PostHog funnel `email_capture_surface_shown` → `newsletter_signup`
 * joins on `source` / `surface_family`. Extra dimensions go in `props`.
 *
 * Non-email surfaces (blog CTA cards) pass `options.emit: analytics.ctaSurfaceShown`
 * to fire `cta_surface_shown` keyed on `cta_location` instead, reusing this
 * observer rather than duplicating it. The default keeps every existing caller
 * on `email_capture_surface_shown` unchanged.
 *
 * Rules-of-hooks: this hook is always called unconditionally. Callers that
 * self-gate (auth / dismissed) must use the wrapper pattern — keep the gate in
 * an outer component and render an inner component that owns this hook only when
 * the surface is actually shown — rather than gating the hook itself. `enabled`
 * is a belt-and-suspenders guard, not a substitute for the wrapper.
 */
export function useImpressionEvent<T extends HTMLElement = HTMLDivElement>(
  source: string,
  props?: EventProperties,
  options?: {
    threshold?: number
    enabled?: boolean
    /** Emitter override. Defaults to the email-capture impression event. */
    emit?: (key: string, props?: EventProperties) => void
  },
): RefObject<T | null> {
  const ref = useRef<T | null>(null)
  const firedRef = useRef(false)
  // Which `source` the once-per-mount lock was taken for. Without this the lock
  // is permanent, and a surface whose source changes IN PLACE reports its
  // impression against the old value while its click reports the new one —
  // clicks with no impressions, on the location you most want a rate for.
  // Real case: useWeedIdCapability resolves async, so a weed post first paints
  // the diagnose fallback and then swaps to the weed identifier. Same component,
  // same position, so React updates props instead of remounting and the ref
  // survives. (Found in review, 2026-07-22.)
  const firedSourceRef = useRef<string | null>(null)
  const enabled = options?.enabled ?? true
  const threshold = options?.threshold ?? 0.5

  // Keep the latest props without re-running the observer effect (which would
  // risk double-firing); the firedRef guard is the real once-per-mount lock.
  const propsRef = useRef(props)
  propsRef.current = props

  // Same treatment for the emitter: an inline arrow at the call site would
  // otherwise change identity every render and re-run the effect.
  const emitRef = useRef(options?.emit)
  emitRef.current = options?.emit

  useEffect(() => {
    if (!enabled) return
    // Re-arm when the surface starts describing a different thing. `source` is
    // already a dep, so this effect re-runs; without releasing the lock it would
    // return immediately and the new source would never be counted.
    if (firedSourceRef.current !== null && firedSourceRef.current !== source) {
      firedRef.current = false
    }
    if (firedRef.current) return
    const el = ref.current
    if (!el) return

    const fire = () => {
      if (firedRef.current) return
      firedRef.current = true
      firedSourceRef.current = source
      ;(emitRef.current ?? analytics.surfaceShown)(source, propsRef.current)
    }

    // Environments without IntersectionObserver (older/test): count the mount.
    if (typeof IntersectionObserver === 'undefined') {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            fire()
            observer.disconnect()
            break
          }
        }
      },
      { threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [enabled, source, threshold])

  return ref
}
